import {AssetSnapshot, AssetType} from '@hconnect/common/types'
import {flow} from 'lodash'
import {Moment} from 'moment-timezone'
import {useCallback, useMemo} from 'react'

import {getActiveAssets, getAssetsFromHistoryByDate, sortAssets} from '../selectors'

import {useAssetsHistoryQuery, type MomentRange} from './useAssetsHistoryQuery'

type AssetSelector = (assets: AssetSnapshot[]) => AssetSnapshot[]

const excludeBaseLoad = (assets: AssetSnapshot[]) =>
  assets.filter((asset) => asset.type !== AssetType.BaseLoad)

interface UseHistoryAssetsQueryParams {
  plantId: string
  timeFrame: MomentRange
  date?: Moment
  sorted?: boolean
  isBaseLoadExcluded?: boolean
  throwOnError?: boolean
}

export const useHistoryAssetsQuery = ({
  plantId,
  timeFrame,
  date,
  sorted = false,
  isBaseLoadExcluded = true,
  throwOnError = true
}: UseHistoryAssetsQueryParams) => {
  const [, to] = timeFrame
  const selectors: AssetSelector[] = useMemo(
    () => [
      getActiveAssets,
      ...(isBaseLoadExcluded ? [excludeBaseLoad] : []),
      ...(sorted ? [sortAssets] : [])
    ],
    [isBaseLoadExcluded, sorted]
  )

  const combinedSelector = useCallback(
    (assets: AssetSnapshot[]) => flow(...selectors)(assets) as AssetSnapshot[],
    [selectors]
  )

  return useAssetsHistoryQuery({
    plantId,
    timeFrame,
    throwOnError,
    select: (assetsHistory) => {
      const assetsFromHistory = getAssetsFromHistoryByDate(assetsHistory, date ?? to)
      return combinedSelector(assetsFromHistory)
    }
  })
}
